// src/services/sbSearchTermService.ts
import { db } from '@/config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';

export interface SBSearchTermRecord {
  date: string;
  campaignName: string;
  campaignId: string;
  adGroupName: string;
  adGroupId: string;
  targeting: string;
  matchType: string;
  customerSearchTerm: string;
  searchTerm?: string; // Alias for customerSearchTerm
  impressions: number;
  viewableImpressions: number;
  clicks: number;
  spend: number;
  cost?: number; // Alias for spend
  sales14d: number;
  orders14d: number;
  units14d: number;
}

export interface SearchFilters {
  startDate: string;
  endDate: string;
  campaignName?: string;
  searchTerm?: string;
  minClicks?: number;
  minSpend?: number;
}

export interface SearchResult {
  records: SBSearchTermRecord[];
  totalRecords: number;
  hasMore: boolean;
}

/**
 * Fetch SB Search Term records with filters
 */
export async function fetchSBSearchTermData(
  company: string,
  filters: SearchFilters,
  pageSize: number = 100
): Promise<SearchResult> {
  console.log(`📊 Fetching SB Search Term data for ${company}`, filters);
  
  try {
    // Build query
    let q = query(
      collection(db, 'amazon_reports', company, 'sbSearchTerm'),
      where('date', '>=', filters.startDate),
      where('date', '<=', filters.endDate),
      orderBy('date', 'desc'),
      limit(pageSize + 1) // Fetch one extra to check if there's more
    );
    
    // Execute query
    const snapshot = await getDocs(q);
    
    // Process results
    const records = snapshot.docs
      .slice(0, pageSize)
      .map(doc => {
        const data = doc.data();
        return {
          date: data.date,
          campaignName: data.campaignName,
          campaignId: data.campaignId,
          adGroupName: data.adGroupName,
          adGroupId: data.adGroupId,
          targeting: data.targeting || '',
          matchType: data.matchType,
          customerSearchTerm: data.customerSearchTerm || data.searchTerm || data.query || '',
          impressions: data.impressions || 0,
          viewableImpressions: data.viewableImpressions || 0,
          clicks: data.clicks || 0,
          spend: data.spend || data.cost || 0,
          sales14d: data.sales14d || data.sales || 0,
          orders14d: data.orders14d || data.purchases14d || data.purchases || 0,
          units14d: data.units14d || 0
        } as SBSearchTermRecord;
      });
    
    // Client-side filtering for fields that can't be indexed
    let filteredRecords = records;
    
    if (filters.campaignName) {
      const term = filters.campaignName.toLowerCase();
      filteredRecords = filteredRecords.filter(r => 
        r.campaignName?.toLowerCase().includes(term)
      );
    }
    
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filteredRecords = filteredRecords.filter(r => 
        r.customerSearchTerm?.toLowerCase().includes(term)
      );
    }
    
    if (filters.minClicks) {
      filteredRecords = filteredRecords.filter(r => 
        r.clicks >= filters.minClicks!
      );
    }
    
    if (filters.minSpend) {
      filteredRecords = filteredRecords.filter(r => 
        r.spend >= filters.minSpend!
      );
    }
    
    console.log(`✅ Fetched ${filteredRecords.length} SB Search Term records`);
    
    return {
      records: filteredRecords,
      totalRecords: filteredRecords.length,
      hasMore: snapshot.docs.length > pageSize
    };
    
  } catch (error) {
    console.error('❌ Error fetching SB Search Term data:', error);
    throw error;
  }
}

/**
 * Fetch summary for date range
 */
export async function fetchSBSearchTermSummary(
  company: string,
  startDate: string,
  endDate: string
): Promise<any[]> {
  try {
    const summariesRef = collection(
      db,
      'amazon_reports',
      company,
      'sbSearchTerm',
      'summaries',
      'daily'
    );
    
    const q = query(
      summariesRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
    
  } catch (error) {
    console.error('❌ Error fetching summary:', error);
    throw error;
  }
}

/**
 * Profile status types
 */
export interface ProfileStatus {
  isSetup: boolean;
  isProcessing: boolean;
  hasReports: boolean;
  lastSyncAt?: Date;
  statusMessage?: string;
  canGenerate: boolean;
}

/**
 * Check if a profile/company already has reports set up
 * Returns status information to determine button state
 */
export async function checkProfileStatus(
  companyName: string,
  profileId: string
): Promise<ProfileStatus> {
  try {
    console.log(`🔍 Checking status for ${companyName} (${profileId})`);
    
    // Check if company has an active connection with this profile
    const connectionsQuery = query(
      collection(db, 'amazon_connections'),
      where('companyName', '==', companyName),
      where('profileId', '==', profileId),
      where('isActive', '==', true)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const hasConnection = !connectionsSnapshot.empty;
    
    if (!hasConnection) {
      return {
        isSetup: false,
        isProcessing: false,
        hasReports: false,
        canGenerate: true,
        statusMessage: undefined
      };
    }
    
    // Check for pending/processing reports
    const pendingReportsQuery = query(
      collection(db, 'pending_amazon_reports'),
      where('companyName', '==', companyName),
      where('profileId', '==', profileId),
      where('status', 'in', ['pending', 'processing'])
    );
    
    const pendingSnapshot = await getDocs(pendingReportsQuery);
    const hasPendingReports = !pendingSnapshot.empty;
    
    // Check processing status document
    const statusDocRef = doc(db, 'amazon_reports', companyName, 'status', 'processing_status');
    const statusDoc = await getDoc(statusDocRef);
    const statusData = statusDoc.exists() ? statusDoc.data() : null;
    
    const isProcessing = statusData?.isProcessing === true || hasPendingReports;
    
    // Check if company has any report data
    const connectionData = connectionsSnapshot.docs[0]?.data();
    const lastSyncAt = connectionData?.lastSyncAt?.toDate();
    const hasReports = !!lastSyncAt;
    
    // Determine status message
    let statusMessage: string | undefined;
    if (isProcessing) {
      const totalReports = statusData?.totalReports || 0;
      const completedReports = statusData?.completedReports || 0;
      statusMessage = `Generating reports (${completedReports}/${totalReports})`;
    } else if (hasReports && lastSyncAt) {
      statusMessage = `In daily sync queue (last: ${lastSyncAt.toLocaleDateString()})`;
    } else if (hasConnection) {
      statusMessage = 'Setup complete - awaiting first sync';
    }
    
    return {
      isSetup: hasConnection,
      isProcessing,
      hasReports,
      lastSyncAt,
      statusMessage,
      canGenerate: !hasConnection // Can only generate if NOT already set up
    };
    
  } catch (error) {
    console.error('❌ Error checking profile status:', error);
    // On error, allow generation attempt
    return {
      isSetup: false,
      isProcessing: false,
      hasReports: false,
      canGenerate: true,
      statusMessage: undefined
    };
  }
}

/**
 * Check status for multiple profiles at once
 */
export async function checkMultipleProfileStatuses(
  companyName: string,
  profileIds: string[]
): Promise<Map<string, ProfileStatus>> {
  const statusMap = new Map<string, ProfileStatus>();
  
  try {
    // Check all profiles in parallel
    const statusPromises = profileIds.map(profileId =>
      checkProfileStatus(companyName, profileId)
        .then(status => ({ profileId, status }))
    );
    
    const results = await Promise.all(statusPromises);
    
    results.forEach(({ profileId, status }) => {
      statusMap.set(profileId, status);
    });
    
  } catch (error) {
    console.error('❌ Error checking multiple profile statuses:', error);
    // On error, set all to allow generation
    profileIds.forEach(profileId => {
      statusMap.set(profileId, {
        isSetup: false,
        isProcessing: false,
        hasReports: false,
        canGenerate: true
      });
    });
  }
  
  return statusMap;
}